import { Button, ButtonProps } from '@mantine/core';
import { useFormContext } from 'react-hook-form';
import { FormState } from 'react-hook-form/dist/types/form';

type CallBackOrValue<T> = T | ((state: FormState<any>) => T);

/**
 * ButtonFormProps
 */
export type ButtonFormProps = Omit<ButtonProps, 'disabled'> & {
  disabled?: CallBackOrValue<ButtonProps['disabled']>;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

/**
 * FormButton component
 * This component is used to submit form, it will show loading state when form is submitting.
 * This component must be used inside Form component.
 *
 * @example
 * ```tsx
 * <Form onSubmit={onSubmit}>
 *  <FormButton type="submit">Submit</FormButton>
 * </Form>
 * ```
 * @example
 * ```tsx
 * <Form onSubmit={onSubmit}>
 *   <FormButton type="submit" color="blue">Submit</FormButton>
 *   <FormButton type="button" color="gray">Cancel</FormButton>
 *   <FormButton type="reset" color="red">Reset</FormButton>
 *   <FormButton type="button" color="green">Custom action</FormButton>
 * </Form>
 * ```
 *
 * @public
 * @param children - button content
 * @param disabled - button disabled based on form state
 * @param props - button props
 */
export const FormButton: React.FC<ButtonFormProps> = ({
  children,
  disabled,
  ...props
}) => {
  const isSubmitButton = props.type === 'submit';
  const { formState } = useFormContext();
  const { isSubmitting } = formState;

  const callbackOrValue = (fnOrValue: any) => {
    if (typeof fnOrValue === 'function') {
      return fnOrValue(formState);
    }
    return fnOrValue;
  };

  return (
    <Button
      loading={isSubmitButton && isSubmitting}
      disabled={callbackOrValue(disabled)}
      {...props}
    >
      {children}
    </Button>
  );
};

export default FormButton;
